import { motion } from 'framer-motion';
import { 
  FileCheck2, 
  ThermometerSun, 
  Database, 
  GraduationCap,
  ShieldCheck
} from 'lucide-react';

const standards = [
  {
    icon: FileCheck2,
    title: 'OEM Service Information',
    description: 'All calibrations performed using manufacturer-published service information and procedures.',
  },
  {
    icon: ThermometerSun,
    title: 'Environment Controls',
    description: 'Controlled lighting, level flooring, and specified clearance zones for accurate calibration.',
  },
  {
    icon: Database,
    title: 'Record Retention',
    description: 'Complete digital records maintained for traceability and audit compliance.',
  },
  {
    icon: GraduationCap,
    title: 'Technician Competency',
    description: 'All technicians hold relevant certifications and complete ongoing ADAS training.',
  },
];

export const Compliance = () => {
  return (
    <section
      id="compliance"
      data-testid="compliance-section"
      className="py-20 md:py-28 bg-slate-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content - Standards Cards */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="order-2 lg:order-1"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {standards.map((standard, index) => {
                const Icon = standard.icon;
                return (
                  <motion.div
                    key={standard.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-white p-5 rounded-lg border border-slate-200 hover:shadow-md transition-shadow"
                    data-testid={`compliance-card-${index}`}
                  >
                    <div className="w-10 h-10 bg-blue-600/10 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-blue-600" />
                    </div>
                    <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">
                      {standard.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      {standard.description}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Right Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="order-1 lg:order-2"
          >
            <span className="text-blue-600 text-sm font-semibold tracking-widest uppercase mb-4 block">
              Compliance & Standards
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-6">
              Audit-Ready
              <span className="block text-blue-600">Governance</span>
            </h2>
            <p className="text-slate-600 text-base sm:text-lg mb-8 leading-relaxed">
              Our compliance framework is designed to withstand scrutiny from insurance 
              technical auditors. We maintain documented procedures, verifiable 
              training records, and complete calibration traceability.
            </p>

            {/* Certification Badge */}
            <div className="flex items-center gap-4 p-4 bg-white rounded-lg border border-slate-200">
              <div className="w-14 h-14 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-7 h-7 text-white" />
              </div>
              <div>
                <h4 className="font-heading text-lg font-bold text-slate-900">
                  Insurer Recognised
                </h4>
                <p className="text-slate-600 text-sm">
                  Calibration procedures accepted by major Australian insurers
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
